import React, { memo } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

const ROWS_PER_PAGE = 40;

/* =========================
   STYLES
========================= */
const styles = StyleSheet.create({
  page: { padding: 20, fontSize: 9, fontFamily: "Helvetica", paddingTop: 6 },

  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },

  schoolInfo: { flex: 1, alignItems: "center", marginLeft: -15 },

  table: {
    fontSize: 8,
    borderWidth: 1,
    marginTop: 4,
  },

  tableRow: { flexDirection: "row" },

  cell: {
    borderWidth: 1,
    padding: 3,
  },

  footer: {
    position: "absolute",
    bottom: 10,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 8,
  },

  label: { fontWeight: "bold" },
});

/* =========================
   MEMOIZED ROW
========================= */
const TableRow = memo(({ student, index }) => (
  <View style={styles.tableRow}>
    <Text style={[styles.cell, { width: "6%" }]}>{index}</Text>
    <Text style={[styles.cell, { width: "14%" }]}>
      {student.candidatE_ID || ""}
    </Text>
    <Text style={[styles.cell, { width: "22%" }]}>
      {student.candidatE_NAME || ""}
    </Text>
    <Text style={[styles.cell, { width: "8%" }]}>{student.gender || ""}</Text>
    <Text style={[styles.cell, { width: "10%" }]}>
      {student.castE_NAME || ""}
    </Text>
    <Text style={[styles.cell, { width: "10%" }]}>
      {student.prinT_CATEGORY || ""}
    </Text>
    <Text style={[styles.cell, { width: "10%", textAlign: "right" }]}>
      {student.examFee.toFixed(2)}
    </Text>
    <Text style={[styles.cell, { width: "10%", textAlign: "right" }]}>
      {student.lateFee.toFixed(2)}
    </Text>
    <Text style={[styles.cell, { width: "10%", textAlign: "right" }]}>
      {student.totalFee.toFixed(2)}
    </Text>
  </View>
));

/* =========================
   PDF DOCUMENT
========================= */
const AnnexurePDF = ({
  pages,
  modalchallan,
  schoolCode,
  schoolName,
  annexureDateTime,
}) => {
  const grandTotal = pages.flat().reduce((sum, s) => sum + s.totalFee, 0);

  return (
    <Document>
      {pages.map((pageData, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          {/* ================= HEADER (REPEATS ON EVERY PAGE) ================= */}
          <View style={{ alignSelf: "flex-end", marginBottom: 4 }}>
            <Text style={{ fontSize: 8 }}>
              <Text style={{ fontWeight: "bold" }}>Generated On:</Text>{" "}
              {annexureDateTime}
            </Text>
          </View>

          <View style={styles.header}>
            <Image
              src="../assets/images/jac-academic-council.png"
              style={{ width: 40, height: 40 }}
            />
            <View style={styles.schoolInfo}>
              <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                Jharkhand Academic Council, Ranchi
              </Text>
              <Text style={{ fontSize: 10 }}>
                <Text style={{ fontWeight: "bold" }}>Class IX Annexure -</Text>{" "}
                2026 (Annual)
              </Text>
              <Text style={{ fontSize: 10 }}>
                <Text style={styles.label}>School Code:</Text> {schoolCode}
              </Text>
              <Text style={{ fontSize: 10 }}>
                <Text style={styles.label}>School Name:</Text> {schoolName}
              </Text>
            </View>
          </View>

          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 6,
            }}
          >
            <Text>
              <Text style={styles.label}>Challan No:</Text>{" "}
              {modalchallan.challaN_NO}
            </Text>
            <Text>
              <Text style={styles.label}>Challan Generated On:</Text>{" "}
              {modalchallan.challaN_ENTRY_DATE} |{" "}
              {modalchallan.challaN_ENTRY_TIME}
            </Text>
          </View>

          {/* ================= TABLE ================= */}
          <View style={styles.table}>
            {/* TABLE HEADER */}
            <View style={[styles.tableRow, { fontWeight: "bold" }]}>
              <Text style={[styles.cell, { width: "6%" }]}>SL</Text>
              <Text style={[styles.cell, { width: "14%" }]}>Candidate ID</Text>
              <Text style={[styles.cell, { width: "22%" }]}>
                Candidate Name
              </Text>
              <Text style={[styles.cell, { width: "8%" }]}>Gender</Text>
              <Text style={[styles.cell, { width: "10%" }]}>Caste</Text>
              <Text style={[styles.cell, { width: "10%" }]}>Category</Text>
              <Text style={[styles.cell, { width: "10%" }]}>Reg. Fees</Text>
              <Text style={[styles.cell, { width: "10%" }]}>Late Fees</Text>
              <Text style={[styles.cell, { width: "10%" }]}>Total</Text>
            </View>

            {/* TABLE ROWS */}
            {pageData.map((student, idx) => (
              <TableRow
                key={idx}
                student={student}
                index={pageIndex * ROWS_PER_PAGE + idx + 1}
              />
            ))}
          </View>

          {/* ================= GRAND TOTAL ONLY ON LAST PAGE ================= */}
          {pageIndex === pages.length - 1 && (
            <Text
              style={{
                marginTop: 6,
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              Total Fees: {grandTotal.toFixed(2)}
            </Text>
          )}

          {/* ================= FOOTER ================= */}
          <Text
            style={styles.footer}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
            fixed
          />
        </Page>
      ))}
    </Document>
  );
};

export default AnnexurePDF;
